//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// login-btn imports
// chrome-welcome-card imports
import { IContainer, IProps, IState } from "./ChromeLogin.d";
import * as s from "./ChromeLogin.styles";
import { chromeLoginFragments } from "./ChromeLogin.gql";
// other internals
import { AuthPermissionInfoCard } from "@components/auth-permission-info-card";
import { LoginBtnNoRouter } from "@components/login-btn";
import { H2, H3, P, A } from "@ui";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { logoClockWhite } from "@img/logos";
import { loginUserChrome } from "@mutations";
import { SidebarDialog } from "@clockwise/web-commons/src/components/sidebar-dialog";
import { CookieDialog } from "@clockwise/web-commons/src/components/cookie-dialog";
// util
import { page, PageEvents, track, TrackingEvents } from "@util/analytics.util";
import { isPersonalDomain } from "@util/email.util";
import { PostMessageManager } from "@util/post-message.util";
import { paths, supportEmail } from "@constants/site.constant";
// material-ui
import { Button, DialogActions, DialogContent, LinearProgress } from "@material-ui/core";

//////////////////
// COMPONENT
//////////////////
export class ChromeLoginComponent extends React.Component<IProps, IState> {
  private permissionInfoCardTimer: ReturnType<typeof setTimeout>;
  constructor(props: IProps) {
    super(props);
    this.state = {
      loggingIn: false,
      hidePermissionInfoCard: true,
      hideCookieErrorDialog: true,
      hideGenericErrorDialog: true,
    };
  }

  public componentDidMount() {
    page(PageEvents.CHROME_LOGIN);
  }

  private onStartLogin = () => {
    clearTimeout(this.permissionInfoCardTimer);
    track(TrackingEvents.AUTH.BEGIN_LOGIN);
    this.setState({ loggingIn: true });
    if (this.props.onStart) {
      this.props.onStart();
    }
  };

  private onFail = () => {
    track(TrackingEvents.AUTH.LOGIN_FAILED);
    this.setState({ loggingIn: false });
    if (this.props.onFailure) {
      this.props.onFailure();
    }
  };

  private onGoogleFailure = (e: any) => {
    if (e.error === "popup_closed_by_user") {
      this.setState({ hidePermissionInfoCard: false });
    } else if (e.error === "idpiframe_initialization_failed") {
      this.setState({ hideCookieErrorDialog: false });
    } else {
      this.setState({ hideGenericErrorDialog: false });
    }
  };

  private onClickClose = () => {
    PostMessageManager.closeSidebar(true);
  };

  private onClickLogin = () => {
    this.permissionInfoCardTimer = setTimeout(() => {
      this.setState({ hidePermissionInfoCard: false });
    }, 10000);
  };

  private onCloseErrorDialog = () => {
    this.setState({
      hideGenericErrorDialog: true,
    });
  };

  private renderPersonalEmail = () => {
    return (
      <div style={s.content.body}>
        <H2 style={s.content.header}>Please sign in to Clockwise from your work calendar.</H2>
        <Button style={{ marginTop: 20 }} onClick={this.onClickClose} variant="text" fullWidth>
          Hide extension
        </Button>
      </div>
    );
  };

  private renderLogin = () => {
    const { windowUserEmail } = this.props;

    let redirect;
    if (this.props.redirect) {
      redirect = this.props.redirect;
      if (this.props.location && this.props.location.search) {
        redirect += this.props.location.search;
      }
    }

    if (windowUserEmail && isPersonalDomain(windowUserEmail)) {
      return this.renderPersonalEmail();
    }

    return (
      <div style={s.content.body}>
        <H2 style={s.content.header}>Sign in to Clockwise with your work calendar</H2>
        <div style={s.content.button}>
          <LoginBtnNoRouter
            googleStandard
            loginUserMutation={loginUserChrome}
            label="Sign in with Google"
            style={s.signupBtn.base}
            labelStyle={s.signupBtn.label}
            redirect={redirect}
            onStart={() => this.onStartLogin()}
            onSuccess={this.props.onSuccess}
            onFailure={() => this.onFail()}
            onGoogleFailure={this.onGoogleFailure}
            onClick={() => this.onClickLogin()}
          />
        </div>
        {this.state.loggingIn ? (
          <LinearProgress style={s.linearProgress} variant="indeterminate" />
        ) : (
          ""
        )}
        <P style={s.content.agreement}>
          By signing in, you agree to Clockwise's{" "}
          <A style={s.content.link} target="_blank" href={paths.terms}>
            Terms of Service
          </A>{" "}
          and{" "}
          <A style={s.content.link} target="_blank" href={paths.privacy}>
            Privacy Policy
          </A>
          .
        </P>
      </div>
    );
  };

  private renderCookieDialog() {
    return (
      <CookieDialog
        isChromeExtension={this.props.isChromeExtension}
        open={!this.state.hideCookieErrorDialog}
      />
    );
  }

  private renderErrorDialog() {
    return (
      <SidebarDialog
        open={!this.state.hideGenericErrorDialog}
        onClose={this.onCloseErrorDialog}
        isChromeExtension={this.props.isChromeExtension}
      >
        <DialogContent>
          <H3 style={s.dialog.header}>Clockwise was unable to connect your Google account</H3>
          <div style={s.dialog.description}>
            There could be restrictions on your GSuite organization settings that would prevent
            Clockwise from accessing needed permissions.
          </div>
          <div style={s.dialog.learnMore}>
            Please{" "}
            <A href={`mailto:${supportEmail}`} target="_blank">
              reach out to our support team
            </A>{" "}
            and we will do our best to help get you connected.
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={this.onCloseErrorDialog}>
            Got it
          </Button>
        </DialogActions>
      </SidebarDialog>
    );
  }

  private renderAuthErrorDialog() {
    if (!this.state.hideCookieErrorDialog) {
      return this.renderCookieDialog();
    } else if (!this.state.hideGenericErrorDialog) {
      return this.renderErrorDialog();
    }

    return null;
  }

  public render() {
    return (
      <div style={s.container}>
        <div style={s.content.container}>
          <div style={s.content.hero}>
            <img src={logoClockWhite} style={s.content.logo} />
          </div>
          {this.renderLogin()}
        </div>
        <AuthPermissionInfoCard hidden={this.state.hidePermissionInfoCard} />
        {this.renderAuthErrorDialog()}
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: any, _ownProps: IContainer) {
  return {
    windowUserEmail: state.webExtension.windowUserEmail,
    isChromeExtension: state.webExtension.isWebExtension,
  };
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(ChromeLoginComponent);

//////////////////
// RELAY
//////////////////
export const ChromeLogin = createFragmentContainer<IContainer>(
  reduxContainer,
  chromeLoginFragments,
);
